import {
  CustomerPaymentInstrumentCreate,
  Maybe,
  PaymentInstrumentFragment,
  PaymentInstrumentStatus,
  PaymentInstrumentType,
} from '../../graphql/types.generated';
import { PaymentInstrument } from './types';
import { paymentTypeToPaymentIconMap } from './constants';
import { type IPaymentProviderExternalRequestData } from '../../lib/payments/PaymentProvider/types';

export const isPaymentInstrumentActive = (
  paymentInstrument: Maybe<PaymentInstrumentFragment>,
): paymentInstrument is PaymentInstrumentFragment => {
  return paymentInstrument?.status === PaymentInstrumentStatus.Active;
};

/**
 * This function works as a sub string search
 * @param paymentType look at GraphQL Schema
 */
export const isGoogleOrApplePaymentInstrument = (paymentType?: PaymentInstrumentType | null) => {
  if (!paymentType) {
    return false;
  }

  return (
    paymentType.includes(PaymentInstrumentType.GooglePay) ||
    paymentType.includes(PaymentInstrumentType.ApplePay) ||
    paymentType.includes(PaymentInstrumentType.GoogleWallet)
  );
};

export const isOpenLoopPaymentInstrument = (paymentType?: PaymentInstrumentType | null) =>
  !isGoogleOrApplePaymentInstrument(paymentType);

export const mapPaymentInstrumentFragmentToPaymentInstrument = (
  fragment: PaymentInstrumentFragment,
): PaymentInstrument => {
  return {
    alias: fragment.alias,
    paymentType: fragment.paymentType,
    accountNumber: fragment.accountNumber,
    providerNickName: fragment.providerNickName,
    last4: fragment.displayNumber,
    // TODO: uncomment and start using it as soon as you find a nice way to merge on device and API response
    // isDefault: !!instrument.isDefault,
    address: fragment?.address?.zipCode
      ? {
          zipCode: fragment.address.zipCode,
        }
      : null,
    uuid: fragment.uuid,
    displayName: fragment.displayNumber || '',
  };
};

export interface IBuildCreatePaymentInstrumentAddressParams {
  nonce: string;
  paymentType: PaymentInstrumentType | null;
  oAuthToken: string | null;
  zipCode: string;
  cardNumber: string;
  /**
   * The last two digits of the expiration year
   */
  expirationYear: string;
  expirationMonth: string;
  externalRequestData: IPaymentProviderExternalRequestData;
}

const FIRST6_LENGTH = 6;
const LAST4_LENGTH = 4;
const getCardNumberAttributes = (cardNumber: string) => {
  const first6 = cardNumber.slice(0, FIRST6_LENGTH);
  const last4 = cardNumber.slice(cardNumber.length - LAST4_LENGTH);
  return { first6, last4 };
};

export const buildCreatePaymentInstrumentPayload = (
  params: IBuildCreatePaymentInstrumentAddressParams,
): CustomerPaymentInstrumentCreate => {
  const { first6, last4 } = getCardNumberAttributes(params.cardNumber);

  return {
    last4,
    binRange: first6,
    nonce: params.nonce,
    paymentType: params.paymentType,
    fiservOAuthToken: params.oAuthToken,
    name: null,
    alias: null,
    accountNumber: null,
    address: {
      zipCode: params.zipCode,
      alias: null,
      street1: null,
      street2: null,
      city: null,
      state: null,
      geoLocation: null,
    },
    expiration: {
      expirationMonth: params.expirationMonth,
      expirationYear: params.expirationYear,
    },
    encryptedPaymentInstrumentDetails: null,
    // Passing `setAsDefault: null` persists default logic on API side
    setAsDefault: null,
    externalRequestData: params.externalRequestData ?? null,
  };
};

// TODO: get reid of "component" and start using "rendered" element
// TODO: start using all over the project
// TODO: add typing
export const getPaymentInstrumentListItemInfo = (paymentInstrument: PaymentInstrument) => {
  const { paymentType, displayName, last4 } = paymentInstrument;
  const PaymentInstrumentTypeIcon = paymentType ? paymentTypeToPaymentIconMap[paymentType] : null;
  const paymentInstrumentName = last4 ? `•••• ${last4}` : displayName || 'Unknown';
  return { PaymentInstrumentTypeIcon, paymentInstrumentName };
};
